import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { ApiUser } from '@vend/types'
import { persistReducer } from 'redux-persist'
import { TOUR_TYPE } from '../../../constants'
import { RootState } from '../../store'
import { persistConfig } from './persistToIndexedDb'

export interface PosState {
  activeSaleLocalId?: string | null
  clientId?: string
  features?: Record<string, boolean>
  initialSyncComplete?: boolean
  lastUsedReceiptNumber?: number
  outletId?: string
  paymentRequestSuccess?: {
    paymentRequestId: string
    requestedAmount: number
    timestamp: number
  } | null
  promoHelperClicked?: boolean
  promoHelperSuggestion?: string
  qkLayoutDuplication?: Record<string, boolean>
  receiptNumbersForRegisters?: Record<string, number>
  recentLoggedUserIds?: string[]
  registerId?: string
  registerOpenRemindersSeen?: Record<string, string>
  tourEnabled?: boolean
  tourType?: TOUR_TYPE
  trainingModeEnabled?: boolean
  trainingModeOnboardingState?: boolean
  user?: ApiUser | null
}

type PosStateUpdate = {
  [K in keyof PosState]-?: {
    key: K
    value: PosState[K]
  }
}[keyof PosState]

const RECENT_LOGGED_USER_IDS_LENGTH = 10

const initialState: PosState = {}

const posStateSlice = createSlice({
  name: 'posState',
  initialState,
  reducers: {
    updatePosState(state, action: PayloadAction<PosStateUpdate>) {
      const { key, value } = action.payload
      state[key] = value as any
    },
    setQkLayoutDuplication(state, action: PayloadAction<string>) {
      state.qkLayoutDuplication = {
        ...(state.qkLayoutDuplication || {}),
        [action.payload]: true,
      }
    },
    clearQkLayoutDuplication(state, action: PayloadAction<string>) {
      if (state.qkLayoutDuplication) {
        delete state.qkLayoutDuplication[action.payload]
      }
    },
    setUser(state, action: PayloadAction<ApiUser | null>) {
      const user = action.payload
      state.user = user

      if (user && user.id) {
        const recentUserIds = state.recentLoggedUserIds || []
        const userIndex = recentUserIds.indexOf(user.id)

        if (userIndex > -1) {
          recentUserIds.splice(userIndex, 1)
        }

        state.recentLoggedUserIds = [user.id, ...recentUserIds].slice(
          0,
          RECENT_LOGGED_USER_IDS_LENGTH
        )
      }
    },
    setPaymentRequestSuccess(
      state,
      action: PayloadAction<{
        paymentRequestId: string
        requestedAmount: number
      }>
    ) {
      state.paymentRequestSuccess = {
        paymentRequestId: action.payload.paymentRequestId,
        requestedAmount: action.payload.requestedAmount,
        timestamp: Date.now(),
      }
    },
    clearPaymentRequestSuccess(state) {
      state.paymentRequestSuccess = null
    },
  },
})

export const reducer = persistReducer(persistConfig, posStateSlice.reducer)
export const {
  setQkLayoutDuplication,
  clearQkLayoutDuplication,
  setUser,
  setPaymentRequestSuccess,
  clearPaymentRequestSuccess,
} = posStateSlice.actions

/** @deprecated Rather set properties individually via their own action creators */
export const updatePosState = posStateSlice.actions.updatePosState

/** @deprecated Rather select individual properties */
export const selectPosState = (state: RootState) => state.posState

export const selectActiveRegisterId = (state: RootState) =>
  state.posState.registerId
export const selectActiveOutletId = (state: RootState) =>
  state.posState.outletId
export const selectLoggedInUser = (state: RootState) => state.posState.user
export const selectLoggedInUserId = (state: RootState) =>
  state.posState.user?.id
export const selectTrainingModeEnabled = (state: RootState) =>
  state.posState.trainingModeEnabled
export const selectPaymentRequestSuccess = (state: RootState) =>
  state.posState.paymentRequestSuccess
